const EMAIL_VALIDATION_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe",
    ),
  );
}

class HTMLUpdateUtility {
  constructor() {
    this._preProcessCallbacks = [];
    this._postProcessCallbacks = [];
  }

  addPreProcessCallback(callback) {
    this._preProcessCallbacks.push(callback);
  }

  addPostProcessCallback(callback) {
    this._postProcessCallbacks.push(callback);
  }

  /**
   * Used to swap an HTML node with a new node.
   * The new node is inserted as a previous sibling to the old node, the old node is hidden, and then the old node is removed.
   *
   * The function currently uses a double buffer approach, but this should be replaced by a view transition once it is more widely supported https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API
   */
  viewTransition(oldNode, newContent) {
    this._preProcessCallbacks.forEach((callback) => callback(newContent));

    const newNode = oldNode.cloneNode();
    HTMLUpdateUtility.setInnerHTML(newNode, newContent.innerHTML);
    oldNode.parentNode.insertBefore(newNode, oldNode);
    oldNode.style.display = "none";

    this._postProcessCallbacks.forEach((callback) => callback(newNode));

    setTimeout(() => oldNode.remove(), 1000);
  }

  // Sets inner HTML and reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
  static setInnerHTML(element, html) {
    element.innerHTML = html;
    element.querySelectorAll("script").forEach((oldScriptTag) => {
      const newScriptTag = document.createElement("script");
      Array.from(oldScriptTag.attributes).forEach((attribute) => {
        newScriptTag.setAttribute(attribute.name, attribute.value);
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }
}

const roleLinks = document.querySelectorAll('[role="link"]');

for (const link of roleLinks) {
  link.addEventListener("click", navigateLink);
  link.addEventListener("keydown", navigateLink);
}

// handles click and keydown events on the link
function navigateLink(e) {
  if (e.type === "click" || e.key === "Enter") {
    const ref = e.target ?? e.srcElement;
    if (ref) {
      window.open(ref.getAttribute("data-href") || ref.getAttribute("href"), "_self");
    }
  }
}

document.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
  summary.setAttribute("aria-expanded", summary.parentNode.hasAttribute("open"));
  summary.setAttribute("aria-haspopup", "true");

  if (summary.nextElementSibling.getAttribute("id")) {
    summary.setAttribute("aria-controls", summary.nextElementSibling.id);
  }

  summary.addEventListener("click", (event) => {
    event.currentTarget.setAttribute("aria-expanded", !event.currentTarget.closest("details").hasAttribute("open"));
  });

  if (summary.closest("header-drawer, menu-drawer")) return;
  summary.parentElement.addEventListener("keyup", onKeyUpEscape);
});

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (event.target !== container && event.target !== last && event.target !== first) return;

    document.addEventListener("keydown", trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener("keydown", trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== "TAB") return; // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if ((event.target === container || event.target === first) && event.shiftKey) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener("focusout", trapFocusHandlers.focusout);
  document.addEventListener("focusin", trapFocusHandlers.focusin);

  elementToFocus.focus();

  if (
    elementToFocus.tagName === "INPUT" &&
    ["search", "text", "email", "url"].includes(elementToFocus.type) &&
    elementToFocus.value
  ) {
    elementToFocus.setSelectionRange(0, elementToFocus.value.length);
  }
}

// Here run the querySelector to figure out if the browser supports :focus-visible or not and run code based on it.
try {
  document.querySelector(":focus-visible");
} catch (e) {
  focusVisiblePolyfill();
}

function focusVisiblePolyfill() {
  const navKeys = [
    "ARROWUP",
    "ARROWDOWN",
    "ARROWLEFT",
    "ARROWRIGHT",
    "TAB",
    "ENTER",
    "SPACE",
    "ESCAPE",
    "HOME",
    "END",
    "PAGEUP",
    "PAGEDOWN",
  ];
  let currentFocusedElement = null;
  let mouseClick = null;

  window.addEventListener("keydown", (event) => {
    if (navKeys.includes(event.code.toUpperCase())) {
      mouseClick = false;
    }
  });

  window.addEventListener("mousedown", (event) => {
    mouseClick = true;
  });

  window.addEventListener(
    "focus",
    () => {
      if (currentFocusedElement) currentFocusedElement.classList.remove("focused");

      if (mouseClick) return;

      currentFocusedElement = document.activeElement;
      currentFocusedElement.classList.add("focused");
    },
    true,
  );
}

function pauseAllMedia() {
  document.querySelectorAll(".js-youtube").forEach((video) => {
    video.contentWindow.postMessage('{"event":"command","func":"' + "pauseVideo" + '","args":""}', "*");
  });
  document.querySelectorAll(".js-vimeo").forEach((video) => {
    video.contentWindow.postMessage('{"method":"pause"}', "*");
  });
  document.querySelectorAll("video").forEach((video) => video.pause());
  document.querySelectorAll("product-model").forEach((model) => {
    if (model.modelViewerUI) model.modelViewerUI.pause();
  });
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener("focusin", trapFocusHandlers.focusin);
  document.removeEventListener("focusout", trapFocusHandlers.focusout);
  document.removeEventListener("keydown", trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== "ESCAPE") return;

  const openDetailsElement = event.target.closest("details[open]");
  if (!openDetailsElement) return;

  const summaryElement = openDetailsElement.querySelector("summary");
  openDetailsElement.removeAttribute("open");
  summaryElement.setAttribute("aria-expanded", false);
  summaryElement.focus();
}

class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector("input");
    this.changeEvent = new Event("change", { bubbles: true });
    this.input.addEventListener("change", this.onInputChange.bind(this));
    this.input.addEventListener("keydown", this.onInputKeydown.bind(this));
    this.querySelectorAll("button").forEach((button) =>
      button.addEventListener("click", this.onButtonClick.bind(this)),
    );
  }

  quantityUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.validateQtyRules();
    this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, this.validateQtyRules.bind(this));
  }

  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber();
    }
  }

  onInputChange(event) {
    this.validateQtyRules();
  }

  onInputKeydown(event) {
    if (event.key !== "Enter" || !this.classList.contains("cart-quantity")) return;

    // Cart quantity inputs sit inside the cart form, whose only submit button is
    // the checkout button, so implicit submission would send the shopper to
    // checkout. Blur instead: that commits the value and the change event
    // updates the line quantity.
    event.preventDefault();
    this.input.blur();
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    if (event.target.name === "plus") {
      if (parseInt(this.input.dataset.min) > parseInt(this.input.step) && this.input.value == 0) {
        this.input.value = this.input.dataset.min;
      } else {
        this.input.stepUp();
      }
    } else {
      this.input.stepDown();
    }

    if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);

    if (this.input.dataset.min === previousValue && event.target.name === "minus") {
      this.input.value = parseInt(this.input.min);
    }
  }

  validateQtyRules() {
    const value = parseInt(this.input.value);
    if (this.input.min) {
      const buttonMinus = this.querySelector(".quantity__button[name='minus']");
      buttonMinus.classList.toggle("disabled", parseInt(value) <= parseInt(this.input.min));
    }
    if (this.input.max) {
      const max = parseInt(this.input.max);
      const buttonPlus = this.querySelector(".quantity__button[name='plus']");
      buttonPlus.classList.toggle("disabled", value >= max);
    }
  }
}

customElements.define("quantity-input", QuantityInput);

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

function throttle(fn, delay) {
  let lastCall = 0;
  return function (...args) {
    const now = new Date().getTime();
    if (now - lastCall < delay) {
      return;
    }
    lastCall = now;
    return fn(...args);
  };
}

function fetchConfig(type = "json") {
  return {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: `application/${type}`,
    },
  };
}

/*
 * Shopify Common JS
 *
 */
if (typeof window.Shopify == "undefined") {
  window.Shopify = {};
}

Shopify.bind = function (fn, scope) {
  return function () {
    return fn.apply(scope, arguments);
  };
};

Shopify.setSelectorByValue = function (selector, value) {
  for (var i = 0, count = selector.options.length; i < count; i++) {
    var option = selector.options[i];
    if (value == option.value || value == option.innerHTML) {
      selector.selectedIndex = i;
      return i;
    }
  }
};

Shopify.addListener = function (target, eventName, callback) {
  target.addEventListener
    ? target.addEventListener(eventName, callback, false)
    : target.attachEvent("on" + eventName, callback);
};

Shopify.postLink = function (path, options) {
  options = options || {};
  var method = options["method"] || "post";
  var params = options["parameters"] || {};

  var form = document.createElement("form");
  form.setAttribute("method", method);
  form.setAttribute("action", path);

  for (var key in params) {
    var hiddenField = document.createElement("input");
    hiddenField.setAttribute("type", "hidden");
    hiddenField.setAttribute("name", key);
    hiddenField.setAttribute("value", params[key]);
    form.appendChild(hiddenField);
  }
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

Shopify.CountryProvinceSelector = function (country_domid, province_domid, options) {
  this.countryEl = document.getElementById(country_domid);
  this.provinceEl = document.getElementById(province_domid);
  this.provinceContainer = document.getElementById(options["hideElement"] || province_domid);

  Shopify.addListener(this.countryEl, "change", Shopify.bind(this.countryHandler, this));

  this.initCountry();
  this.initProvince();
};

Shopify.CountryProvinceSelector.prototype = {
  initCountry: function () {
    var value = this.countryEl.getAttribute("data-default");
    Shopify.setSelectorByValue(this.countryEl, value);
    this.countryHandler();
  },

  initProvince: function () {
    var value = this.provinceEl.getAttribute("data-default");
    if (value && this.provinceEl.options.length > 0) {
      Shopify.setSelectorByValue(this.provinceEl, value);
    }
  },

  countryHandler: function (e) {
    var opt = this.countryEl.options[this.countryEl.selectedIndex];
    var raw = opt.getAttribute("data-provinces");
    var provinces = JSON.parse(raw);

    this.clearOptions(this.provinceEl);
    if (provinces && provinces.length == 0 && this.provinceContainer) {
      this.provinceContainer.style.display = "none";
    } else {
      for (var i = 0; i < provinces.length; i++) {
        var opt = document.createElement("option");
        opt.value = provinces[i][0];
        opt.innerHTML = provinces[i][1];
        this.provinceEl.appendChild(opt);
      }

      if (this.provinceContainer) {
        this.provinceContainer.style.display = provinces.length === 0 ? "none" : "";
      }
    }
  },

  clearOptions: function (selector) {
    while (selector.firstChild) {
      selector.removeChild(selector.firstChild);
    }
  },

  setOptions: function (selector, values) {
    for (var i = 0, count = values.length; i < values.length; i++) {
      var opt = document.createElement("option");
      opt.value = values[i];
      opt.innerHTML = values[i];
      selector.appendChild(opt);
    }
  },
};

class MenuDrawer extends HTMLElement {
  constructor() {
    super();

    this.mainDetailsToggle = this.querySelector("details");

    this.addEventListener("keyup", this.onKeyUp.bind(this));
    this.addEventListener("focusout", this.onFocusOut.bind(this));
    this.bindEvents();
  }

  bindEvents() {
    this.querySelectorAll("summary").forEach((summary) =>
      summary.addEventListener("click", this.onSummaryClick.bind(this)),
    );
    this.querySelectorAll(
      "button:not(.localization-selector):not(.country-selector__close-button):not(.country-filter__reset-button)",
    ).forEach((button) => button.addEventListener("click", this.onCloseButtonClick.bind(this)));
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== "ESCAPE") return;

    const openDetailsElement = event.target.closest("details[open]");
    if (!openDetailsElement) return;

    openDetailsElement === this.mainDetailsToggle
      ? this.closeMenuDrawer(event, this.mainDetailsToggle.querySelector("summary"))
      : this.closeSubmenu(openDetailsElement);
  }

  onSummaryClick(event) {
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const parentMenuElement = detailsElement.closest(".has-submenu");
    const isOpen = detailsElement.hasAttribute("open");
    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");

    function addTrapFocus() {
      trapFocus(summaryElement.nextElementSibling, detailsElement.querySelector("button"));
      summaryElement.nextElementSibling.removeEventListener("transitionend", addTrapFocus);
    }

    if (detailsElement === this.mainDetailsToggle) {
      if (isOpen) event.preventDefault();
      isOpen ? this.closeMenuDrawer(event, summaryElement) : this.openMenuDrawer(summaryElement);

      if (window.matchMedia("(max-width: 990px)")) {
        document.documentElement.style.setProperty("--viewport-height", `${window.innerHeight}px`);
      }
    } else {
      setTimeout(() => {
        detailsElement.classList.add("menu-opening");
        summaryElement.setAttribute("aria-expanded", true);
        parentMenuElement && parentMenuElement.classList.add("submenu-open");
        !reducedMotion || reducedMotion.matches
          ? addTrapFocus()
          : summaryElement.nextElementSibling.addEventListener("transitionend", addTrapFocus);
      }, 100);
    }
  }

  openMenuDrawer(summaryElement) {
    setTimeout(() => {
      this.mainDetailsToggle.classList.add("menu-opening");
    });
    summaryElement.setAttribute("aria-expanded", true);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus = false) {
    if (event === undefined) return;

    this.mainDetailsToggle.classList.remove("menu-opening");
    this.mainDetailsToggle.querySelectorAll("details").forEach((details) => {
      details.removeAttribute("open");
      details.classList.remove("menu-opening");
    });
    this.mainDetailsToggle.querySelectorAll(".submenu-open").forEach((submenu) => {
      submenu.classList.remove("submenu-open");
    });
    document.body.classList.remove(`overflow-hidden-${this.dataset.breakpoint}`);
    removeTrapFocus(elementToFocus);
    this.closeAnimation(this.mainDetailsToggle);

    if (event instanceof KeyboardEvent) elementToFocus?.setAttribute("aria-expanded", false);
  }

  onFocusOut() {
    setTimeout(() => {
      if (this.mainDetailsToggle.hasAttribute("open") && !this.mainDetailsToggle.contains(document.activeElement))
        this.closeMenuDrawer();
    });
  }

  onCloseButtonClick(event) {
    const detailsElement = event.currentTarget.closest("details");
    this.closeSubmenu(detailsElement);
  }

  closeSubmenu(detailsElement) {
    const parentMenuElement = detailsElement.closest(".submenu-open");
    parentMenuElement && parentMenuElement.classList.remove("submenu-open");
    detailsElement.classList.remove("menu-opening");
    detailsElement.querySelector("summary").setAttribute("aria-expanded", false);
    removeTrapFocus(detailsElement.querySelector("summary"));
    this.closeAnimation(detailsElement);
  }

  closeAnimation(detailsElement) {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        detailsElement.removeAttribute("open");
        if (detailsElement.closest("details[open]")) {
          trapFocus(detailsElement.closest("details[open]"), detailsElement.querySelector("summary"));
        }
      }
    };

    window.requestAnimationFrame(handleAnimation);
  }
}

customElements.define("menu-drawer", MenuDrawer);

class HeaderDrawer extends MenuDrawer {
  constructor() {
    super();
  }

  openMenuDrawer(summaryElement) {
    this.header = this.header || document.querySelector(".section-header");
    this.borderOffset =
      this.borderOffset || this.closest(".header-wrapper").classList.contains("header-wrapper--border-bottom") ? 1 : 0;
    document.documentElement.style.setProperty(
      "--header-bottom-position",
      `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`,
    );
    this.header.classList.add("menu-open");

    setTimeout(() => {
      this.mainDetailsToggle.classList.add("menu-opening");
    });

    summaryElement.setAttribute("aria-expanded", true);
    window.addEventListener("resize", this.onResize);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus) {
    if (!elementToFocus) return;
    super.closeMenuDrawer(event, elementToFocus);
    this.header.classList.remove("menu-open");
    window.removeEventListener("resize", this.onResize);
  }

  onResize = () => {
    this.header &&
      document.documentElement.style.setProperty(
        "--header-bottom-position",
        `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`,
      );
    document.documentElement.style.setProperty("--viewport-height", `${window.innerHeight}px`);
  };
}

customElements.define("header-drawer", HeaderDrawer);

class ModalDialog extends HTMLElement {
  constructor() {
    super();
    this.querySelector('[id^="ModalClose-"]').addEventListener("click", this.hide.bind(this, false));
    this.addEventListener("keyup", (event) => {
      if (event.code.toUpperCase() === "ESCAPE") this.hide();
    });
    if (this.classList.contains("media-modal")) {
      if (this.classList.contains("product-media-modal")) return;
      this.addEventListener("pointerup", (event) => {
        if (event.pointerType === "mouse" && !event.target.closest("deferred-media, product-model")) this.hide();
      });
    } else {
      this.addEventListener("click", (event) => {
        if (event.target === this) this.hide();
      });
    }
  }

  connectedCallback() {
    if (this.moved) return;
    this.moved = true;
    document.body.appendChild(this);
  }

  show(opener) {
    this.openedBy = opener;
    const popup = this.querySelector(".template-popup");
    document.body.classList.add("overflow-hidden");
    this.setAttribute("open", "");
    if (popup) popup.loadContent();
    trapFocus(this, this.querySelector('[role="dialog"]'));
    window.pauseAllMedia();
  }

  hide() {
    document.body.classList.remove("overflow-hidden");
    document.body.dispatchEvent(new CustomEvent("modalClosed"));
    this.removeAttribute("open");
    removeTrapFocus(this.openedBy);
    window.pauseAllMedia();
  }
}
customElements.define("modal-dialog", ModalDialog);

class ModalOpener extends HTMLElement {
  constructor() {
    super();

    const button = this.querySelector("button");

    if (!button) return;
    button.addEventListener("click", () => {
      const modal = document.querySelector(this.getAttribute("data-modal"));
      if (modal) modal.show(button);
    });
  }
}
customElements.define("modal-opener", ModalOpener);

class DeferredMedia extends HTMLElement {
  constructor() {
    super();
    this.autoPlay = this.hasAttribute("data-autoplay");
    const poster = this.querySelector('[id^="Deferred-Poster-"]');

    if (!poster) return;
    if (this.autoPlay) {
      window.addEventListener("load", () => {
        // Only auto-load if visible (skip videos inside hidden modals)
        if (this.offsetParent !== null) {
          this.loadContent();
        }
      });
      return;
    }

    poster.addEventListener("click", this.loadContent.bind(this));
  }

  loadContent(focus = true) {
    if (!this.autoPlay) window.pauseAllMedia();
    if (!this.getAttribute("loaded") && !this.querySelector("video, model-viewer, iframe")) {
      const content = document.createElement("div");
      content.appendChild(this.querySelector("template").content.firstElementChild.cloneNode(true));

      const deferredElement = this.appendChild(content.querySelector("video, model-viewer, iframe"));

      if (deferredElement.nodeName === "VIDEO") {
        // Delay hiding the poster until the video has its first frame ready
        const showVideo = () => {
          this.setAttribute("loaded", true);
          if (focus && !this.autoPlay) deferredElement.focus();
        };
        if (deferredElement.readyState >= 1) {
          showVideo();
        } else {
          deferredElement.addEventListener("loadedmetadata", showVideo, { once: true });
        }
        this.setupSubtleControls(deferredElement);
      } else {
        this.setAttribute("loaded", true);
        if (focus && !this.autoPlay) deferredElement.focus();
      }
    }
  }

  setupSubtleControls(videoElement) {
    if (this.querySelector(".fp-video-controls")) return;

    const hideMute = this.hasAttribute("data-hide-mute");

    if (hideMute) {
      // No mute button — force muted always
      videoElement.muted = true;
      if (this.autoPlay) videoElement.play();
    } else if (this.autoPlay) {
      // Autoplay with mute button — start muted
      videoElement.muted = true;
      videoElement.play();
    } else {
      // Not autoplay with mute button — start unmuted
      videoElement.muted = false;
    }

    const controls = document.createElement("div");
    controls.className = "fp-video-controls";

    // Play/Pause button
    const playPauseBtn = document.createElement("button");
    playPauseBtn.type = "button";
    playPauseBtn.className = "fp-video-controls__btn fp-video-controls__play-pause";
    playPauseBtn.setAttribute("aria-label", videoElement.paused ? "Play video" : "Pause video");
    playPauseBtn.innerHTML = videoElement.paused
      ? '<svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" class="icon icon-play" fill="none" viewBox="0 0 10 14"><path fill-rule="evenodd" clip-rule="evenodd" d="M1.48177 0.814643C0.81532 0.448245 0 0.930414 0 1.69094V12.2081C0 12.991 0.858787 13.4702 1.52503 13.0592L10.5398 7.49813C11.1918 7.09588 11.1679 6.13985 10.4965 5.77075L1.48177 0.814643Z" fill="currentColor"/></svg>'
      : '<svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" class="icon icon-pause" fill="none" viewBox="0 0 10 14"><rect width="3" height="14" fill="currentColor"/><rect x="7" width="3" height="14" fill="currentColor"/></svg>';

    // Mute/Unmute button
    let muteBtn = null;

    if (!hideMute) {
      muteBtn = document.createElement("button");
      muteBtn.type = "button";
      muteBtn.className = "fp-video-controls__btn fp-video-controls__mute";
      muteBtn.setAttribute("aria-label", videoElement.muted ? "Unmute video" : "Mute video");
      muteBtn.innerHTML = videoElement.muted
        ? '<svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" class="icon icon-volume-off" fill="none" viewBox="0 0 24 24"><path d="M11 5L6 9H2v6h4l5 4V5z" fill="currentColor"/><line x1="23" y1="9" x2="17" y2="15" stroke="currentColor" stroke-width="2" stroke-linecap="round"/><line x1="17" y1="9" x2="23" y2="15" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>'
        : '<svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" class="icon icon-volume-on" fill="none" viewBox="0 0 24 24"><path d="M11 5L6 9H2v6h4l5 4V5z" fill="currentColor"/><path d="M15.54 8.46a5 5 0 010 7.07" stroke="currentColor" stroke-width="2" stroke-linecap="round"/><path d="M19.07 4.93a10 10 0 010 14.14" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>';
    }

    controls.appendChild(playPauseBtn);
    if (muteBtn) controls.appendChild(muteBtn);
    this.appendChild(controls);

    const playIcon =
      '<svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" class="icon icon-play" fill="none" viewBox="0 0 10 14"><path fill-rule="evenodd" clip-rule="evenodd" d="M1.48177 0.814643C0.81532 0.448245 0 0.930414 0 1.69094V12.2081C0 12.991 0.858787 13.4702 1.52503 13.0592L10.5398 7.49813C11.1918 7.09588 11.1679 6.13985 10.4965 5.77075L1.48177 0.814643Z" fill="currentColor"/></svg>';
    const pauseIcon =
      '<svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" class="icon icon-pause" fill="none" viewBox="0 0 10 14"><rect width="3" height="14" fill="currentColor"/><rect x="7" width="3" height="14" fill="currentColor"/></svg>';
    const volumeOffIcon =
      '<svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" class="icon icon-volume-off" fill="none" viewBox="0 0 24 24"><path d="M11 5L6 9H2v6h4l5 4V5z" fill="currentColor"/><line x1="23" y1="9" x2="17" y2="15" stroke="currentColor" stroke-width="2" stroke-linecap="round"/><line x1="17" y1="9" x2="23" y2="15" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>';
    const volumeOnIcon =
      '<svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" class="icon icon-volume-on" fill="none" viewBox="0 0 24 24"><path d="M11 5L6 9H2v6h4l5 4V5z" fill="currentColor"/><path d="M15.54 8.46a5 5 0 010 7.07" stroke="currentColor" stroke-width="2" stroke-linecap="round"/><path d="M19.07 4.93a10 10 0 010 14.14" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>';

    function updatePlayPauseState() {
      if (videoElement.paused) {
        playPauseBtn.innerHTML = playIcon;
        playPauseBtn.setAttribute("aria-label", "Play video");
      } else {
        playPauseBtn.innerHTML = pauseIcon;
        playPauseBtn.setAttribute("aria-label", "Pause video");
      }
    }

    function updateMuteState() {
      if (!muteBtn) return;
      if (videoElement.muted) {
        muteBtn.innerHTML = volumeOffIcon;
        muteBtn.setAttribute("aria-label", "Unmute video");
      } else {
        muteBtn.innerHTML = volumeOnIcon;
        muteBtn.setAttribute("aria-label", "Mute video");
      }
    }

    playPauseBtn.addEventListener("click", (e) => {
      e.stopPropagation();
      if (videoElement.paused) {
        videoElement.play();
        // Unmute on play for non-autoplay videos with mute button
        if (!hideMute && !this.autoPlay) {
          videoElement.muted = false;
        }
      } else {
        videoElement.pause();
      }
    });

    if (muteBtn) {
      muteBtn.addEventListener("click", (e) => {
        e.stopPropagation();
        videoElement.muted = !videoElement.muted;
        updateMuteState();
      });
    }

    videoElement.addEventListener("click", (e) => {
      e.preventDefault();
      videoElement.paused ? videoElement.play() : videoElement.pause();
    });

    videoElement.addEventListener("play", updatePlayPauseState);
    videoElement.addEventListener("pause", updatePlayPauseState);
    videoElement.addEventListener("volumechange", updateMuteState);

    // Reposition controls to align with actual video content when object-fit: contain causes letterboxing
    // Hide controls until positioned to prevent visual jump
    controls.style.visibility = "hidden";

    const repositionControls = () => {
      if (!videoElement.videoWidth || !videoElement.videoHeight) return;
      const style = window.getComputedStyle(videoElement);
      if (style.objectFit !== "contain") {
        controls.style.visibility = "";
        return;
      }

      const containerW = videoElement.clientWidth;
      const containerH = videoElement.clientHeight;
      const videoRatio = videoElement.videoWidth / videoElement.videoHeight;
      const containerRatio = containerW / containerH;

      let offsetBottom = 0;
      let offsetLeft = 0;

      if (videoRatio < containerRatio) {
        // Video is narrower — pillarboxed (black bars on sides)
        const renderedW = containerH * videoRatio;
        offsetLeft = (containerW - renderedW) / 2;
      } else {
        // Video is wider — letterboxed (black bars top/bottom)
        const renderedH = containerW / videoRatio;
        offsetBottom = (containerH - renderedH) / 2;
      }

      controls.style.bottom = `calc(${offsetBottom}px + 0.75rem)`;
      controls.style.left = `calc(${offsetLeft}px + 0.75rem)`;
      controls.style.visibility = "";
    };

    videoElement.addEventListener("loadedmetadata", repositionControls);
    window.addEventListener("resize", repositionControls);
    if (videoElement.readyState >= 1) repositionControls();
  }
}

customElements.define("deferred-media", DeferredMedia);

class SliderComponent extends HTMLElement {
  constructor() {
    super();
    this.slider = this.querySelector('[id^="Slider-"]') ?? this.querySelector("#fp-recently-viewed");
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.minimumSliderItemsToStartInfiniteScroll = 7;
    this.enableSliderLooping = false;
    this.isLooping = false; // Prevent multiple simultaneous loops
    this.enableInfiniteLoop = this.hasAttribute("data-enable-infinite-loop");
    this.currentPageElement = this.querySelector(".slider-counter--current");
    this.pageTotalElement = this.querySelector(".slider-counter--total");
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');
    this.sliderIndicatorActive = this.querySelector(".fp-pdp-carousel__indicator-active");
    this.sliderIndicatorWrapper = this.querySelector(".fp-pdp__slider-buttons--bar");
    this.onButtonClick = this.onButtonClick.bind(this);
    this.onIndicatorClick = this.onIndicatorClick.bind(this);
    this.initPages();
  }

  connectedCallback() {
    this.allMediaGalleries = document.querySelectorAll("media-gallery");
    this.mediaGallery = this.allMediaGalleries[0]; // PDP media gallery on the left
    this.productModalMediaGallery = this.allMediaGalleries[1]; // Modal opened when clicking on PDP media gallery

    if (!this.slider || !this.nextButton) return;

    this.resizeObserver = new ResizeObserver((_entries) => {
      this.resetPages();
    });
    this.resizeObserver.observe(this.slider);

    this.debouncedScrollHandler = this.debounce(() => {
      this.update();
    }, 16);

    this.update = this.update.bind(this);
    this.slider.addEventListener("scroll", this.debouncedScrollHandler);
    this.prevButton.addEventListener("click", this.onButtonClick);
    this.nextButton.addEventListener("click", this.onButtonClick);

    if (this.sliderIndicatorWrapper) {
      this.sliderIndicatorWrapper.addEventListener("click", this.onIndicatorClick);
    }
  }

  disconnectedCallback() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }

    if (!this.slider || !this.nextButton) return;

    this.slider.removeEventListener("scroll", this.debouncedScrollHandler);
    this.prevButton.removeEventListener("click", this.onButtonClick);
    this.nextButton.removeEventListener("click", this.onButtonClick);

    if (this.sliderIndicatorWrapper) {
      this.sliderIndicatorWrapper.removeEventListener("click", this.onIndicatorClick);
    }
  }

  initPages() {
    this.sliderItemsToShow = Array.from(this.sliderItems).filter((element) => element.clientWidth > 0);
    if (this.sliderItemsToShow.length < 2) return;
    this.sliderItemOffset = this.sliderItemsToShow[1].offsetLeft - this.sliderItemsToShow[0].offsetLeft;
    this.slidesPerPage = Math.ceil(
      (this.slider.clientWidth - this.sliderItemsToShow[0].offsetLeft) / this.sliderItemOffset,
    );
    this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + 1;
    this.enableInfiniteLoop =
      this.enableInfiniteLoop && this.sliderItems.length >= this.minimumSliderItemsToStartInfiniteScroll;
    if (this.enableInfiniteLoop) {
      this.initInfiniteLoop();
      // Enable buttons for carousel
      this.prevButton.removeAttribute("disabled");
      this.nextButton.removeAttribute("disabled");
    }
    this.update();
  }

  initInfiniteLoop() {
    if (!this.clonesAppended) {
      const [firstItems, lastItems] = this.createClonedSlides();
      this.slider.appendChild(firstItems);
      this.slider.prepend(lastItems);
      this.clonesAppended = true; // we only need to do this once
    }

    // Update clone positions
    this.updateClonePositions();
  }

  updateClonePositions() {
    const firstClone = this.slider.querySelector("[data-first-item]");
    const lastClone = this.slider.querySelector("[data-last-item]");

    if (firstClone && lastClone) {
      // Use offsetLeft for more reliable positioning
      this.firstCloneScrollLeft = firstClone.offsetLeft;
      this.lastCloneScrollLeft = lastClone.offsetLeft;

      // Calculate the exact scroll positions for seamless looping
      // Position to jump to when scrolling forward past the last item
      this.originalStartScroll = this.lastCloneScrollLeft + this.sliderItemOffset;

      // Position to jump to when scrolling backward past the first item
      this.originalEndScroll = this.firstCloneScrollLeft - this.sliderItemOffset;
    }
  }

  createClonedSlides() {
    const firstSetOfFragments = document.createDocumentFragment();
    const lastSetOfFragments = document.createDocumentFragment();

    this.sliderItems.forEach((item, index) => {
      if (index <= this.slidesPerPage) {
        const clone = item.cloneNode(true);
        clone.setAttribute("aria-hidden", "true");
        clone.removeAttribute("id"); // Remove duplicate IDs
        if (index === 0) {
          clone.setAttribute("data-first-item", "true");
        }
        firstSetOfFragments.appendChild(clone);
      }
      if (index >= this.sliderItems.length - (this.slidesPerPage + 1)) {
        const clone = item.cloneNode(true);
        clone.setAttribute("aria-hidden", "true");
        clone.removeAttribute("id"); // Remove duplicate IDs
        if (index === this.sliderItems.length - 1) {
          clone.setAttribute("data-last-item", "true");
        }
        lastSetOfFragments.appendChild(clone);
      }
    });

    return [firstSetOfFragments, lastSetOfFragments];
  }

  resetPages() {
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.initPages();
  }

  update() {
    // Temporarily prevents unneeded updates resulting from variant changes
    // This should be refactored as part of https://github.com/Shopify/dawn/issues/2057
    if (!this.slider || !this.nextButton) return;

    const previousPage = this.currentPage;
    this.currentPage = Math.round(this.slider.scrollLeft / this.sliderItemOffset) + 1;

    this.updateIndicator();

    if (this.currentPageElement && this.pageTotalElement) {
      this.currentPageElement.textContent = this.currentPage;
      this.pageTotalElement.textContent = this.totalPages;
    }

    if (this.currentPage != previousPage) {
      this.dispatchEvent(
        new CustomEvent("slideChanged", {
          detail: {
            currentPage: this.currentPage,
            currentElement: this.sliderItemsToShow[this.currentPage - 1],
          },
        }),
      );
    }

    if (this.enableSliderLooping) return;

    // Infinite loop logic on scroll
    if (this.enableInfiniteLoop && !this.isLooping) {
      this.handleInfiniteLoopScroll();
      return;
    }

    if (this.slider.getAttribute("id")?.includes("Slider-ProductModal-Thumbnails") || this.enableInfiniteLoop) {
      // Don't disable slider buttons on product modal so that we can have infinite looping
      return;
    } else {
      if (this.isSlideVisible(this.sliderItemsToShow[0]) && this.slider.scrollLeft === 0) {
        this.prevButton.setAttribute("disabled", "disabled");
      } else {
        this.prevButton.removeAttribute("disabled");
      }

      if (this.isSlideVisible(this.sliderItemsToShow[this.sliderItemsToShow.length - 1])) {
        this.nextButton.setAttribute("disabled", "disabled");
      } else {
        this.nextButton.removeAttribute("disabled");
      }
    }
  }

  updateIndicator() {
    if (!this.sliderIndicatorActive) return;
    const slideCount = this.sliderItemsToShow?.length || 1;
    this.sliderIndicatorActive.style.width = `${100 / slideCount}%`;
    const maxScroll = this.slider.scrollWidth - this.slider.clientWidth;
    const progress = maxScroll > 0 ? this.slider.scrollLeft / maxScroll : 0;
    const clampedProgress = Math.min(Math.max(progress, 0), 1);
    this.sliderIndicatorActive.style.transform = `translateX(${clampedProgress * (slideCount - 1) * 100}%)`;
  }

  onIndicatorClick(event) {
    event.stopPropagation();
    if (!this.sliderIndicatorWrapper || !this.sliderItemsToShow || this.sliderItemsToShow.length < 2) return;
    const rect = this.sliderIndicatorWrapper.getBoundingClientRect();
    if (rect.width === 0) return;
    const fraction = (event.clientX - rect.left) / rect.width;
    const slideCount = this.sliderItemsToShow.length;
    const index = Math.min(Math.max(Math.floor(fraction * slideCount), 0), slideCount - 1);
    const target = this.sliderItemsToShow[index];
    if (!target) return;
    this.slider.scrollTo({ left: target.offsetLeft, behavior: "smooth" });
  }

  handleInfiniteLoopScroll() {
    const scrollLeft = this.slider.scrollLeft;
    // Calculate buffer zone - snap earlier to prevent blank space
    const bufferZone = this.sliderItemOffset * 0.8;

    // Check if we've scrolled past the end (into the cloned beginning)
    // Snap before reaching the actual clone to prevent visual gap
    if (scrollLeft >= this.firstCloneScrollLeft - bufferZone) {
      this.snapToPosition(this.originalStartScroll);
      return;
    }

    // Check if we've scrolled before the beginning (into the cloned end)
    // Snap before reaching the actual clone to prevent visual gap
    if (scrollLeft <= this.lastCloneScrollLeft + bufferZone) {
      this.snapToPosition(this.originalEndScroll);
      return;
    }
  }

  snapToPosition(position) {
    if (this.isLooping) return; // Prevent multiple snaps

    this.isLooping = true;

    this.slider.style.scrollBehavior = "auto";
    this.slider.scrollLeft = position;

    // Use minimal delay to re-enable smooth scrolling
    requestAnimationFrame(() => {
      this.slider.style.scrollBehavior = "";
      // Allow new loops after a brief moment
      setTimeout(() => {
        this.isLooping = false;
      }, 100);
    });
  }

  isSlideVisible(element, offset = 0) {
    const lastVisibleSlide = this.slider.clientWidth + this.slider.scrollLeft - offset;
    return (
      element?.offsetLeft + element?.clientWidth <= lastVisibleSlide && element?.offsetLeft >= this.slider.scrollLeft
    );
  }

  onButtonClick(event) {
    event.preventDefault();

    if (event.currentTarget.classList.contains("slider-counter__link--dots") && this.mediaGallery) {
      this.mediaGallery.setActiveMedia(event.currentTarget.dataset.target);
      return;
    }

    // Handle product modal infinite looping
    if (event.currentTarget.classList.contains("fp-product-modal__slider-button")) {
      this.handleProductModalInfiniteLoop(event);
      return;
    }

    // Calculate next scroll position
    const step = parseInt(event.currentTarget.dataset.step) || 1;
    const direction = event.currentTarget.name === "next" ? 1 : -1;
    const targetScroll = this.slider.scrollLeft + direction * step * this.sliderItemOffset;

    // Handle infinite loop for buttons
    if (this.enableInfiniteLoop) {
      this.handleInfiniteLoopButton(targetScroll, direction);
    } else {
      this.setSlidePosition(targetScroll);
    }
  }

  handleInfiniteLoopButton(targetScroll, direction) {
    const bufferZone = this.sliderItemOffset * 0.5;

    // Going forward past the last real item
    if (direction > 0 && targetScroll >= this.firstCloneScrollLeft - bufferZone) {
      // Instantly jump without showing the clone
      this.snapToPosition(this.originalStartScroll);
      return;
    }

    // Going backward past the first real item
    if (direction < 0 && targetScroll <= this.lastCloneScrollLeft + bufferZone) {
      this.snapToPosition(this.originalEndScroll);
      return;
    }

    // Normal scroll within bounds
    this.setSlidePosition(targetScroll);
  }

  handleProductModalInfiniteLoop(event) {
    const previousMediaPosition = this.productModalMediaGallery.querySelector(
      '.thumbnail-list__item:has(button[aria-current="true"])',
    )?.dataset.mediaPosition;

    if (!previousMediaPosition) return;

    let nextMediaPosition;
    const thumbnails = this.productModalMediaGallery.querySelectorAll(".thumbnail-list__item");
    const totalThumbnails = thumbnails.length;

    if (event.currentTarget.name === "next") {
      nextMediaPosition = previousMediaPosition == totalThumbnails ? "1" : String(+previousMediaPosition + 1);
    } else {
      nextMediaPosition = previousMediaPosition == "1" ? String(totalThumbnails) : String(+previousMediaPosition - 1);
    }

    const nextMedia = this.productModalMediaGallery.querySelector(
      `.thumbnail-list__item[data-media-position="${nextMediaPosition}"]`,
    );

    if (nextMedia) {
      this.productModalMediaGallery.setActiveMedia(nextMedia.dataset.target);

      // Handle scroll position for looping
      if (previousMediaPosition == "1" && event.currentTarget.name === "previous") {
        this.setSlidePosition(99999);
      } else if (previousMediaPosition == totalThumbnails && event.currentTarget.name === "next") {
        this.setSlidePosition(0);
      }
    }
  }

  setSlidePosition(position, smooth = true, callback = null) {
    if (!smooth) {
      this.slider.style.scrollBehavior = "auto";
    }

    this.slider.scrollTo({
      left: position,
    });

    if (!smooth) {
      setTimeout(() => {
        this.slider.style.scrollBehavior = "";
        if (callback) callback();
      }, 50);
    } else if (callback) {
      callback();
    }
  }

  debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }
}

customElements.define("slider-component", SliderComponent);

class SlideshowComponent extends SliderComponent {
  constructor() {
    super();
    this.sliderControlWrapper = this.querySelector(".slider-buttons");
    this.enableSliderLooping = true;

    if (!this.sliderControlWrapper) return;

    this.sliderFirstItemNode = this.slider.querySelector(".slideshow__slide");
    if (this.sliderItemsToShow.length > 0) this.currentPage = 1;

    this.announcementBarSlider = this.querySelector(".announcement-bar-slider");
    // Value below should match --duration-announcement-bar CSS value
    this.announcerBarAnimationDelay = this.announcementBarSlider ? 250 : 0;

    this.sliderControlLinksArray = Array.from(this.sliderControlWrapper.querySelectorAll(".slider-counter__link"));
    this.sliderControlLinksArray.forEach((link) => link.addEventListener("click", this.linkToSlide.bind(this)));
    this.slider.addEventListener("scroll", this.setSlideVisibility.bind(this));
    this.setSlideVisibility();

    if (this.announcementBarSlider) {
      this.announcementBarArrowButtonWasClicked = false;

      this.reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
      this.reducedMotion.addEventListener("change", () => {
        if (this.slider.getAttribute("data-autoplay") === "true") this.setAutoPlay();
      });

      [this.prevButton, this.nextButton].forEach((button) => {
        button.addEventListener(
          "click",
          () => {
            this.announcementBarArrowButtonWasClicked = true;
          },
          { once: true },
        );
      });
    }

    if (this.slider.getAttribute("data-autoplay") === "true") this.setAutoPlay();
  }

  setAutoPlay() {
    this.autoplaySpeed = this.slider.dataset.speed * 1000;
    this.addEventListener("mouseover", this.focusInHandling.bind(this));
    this.addEventListener("mouseleave", this.focusOutHandling.bind(this));
    this.addEventListener("focusin", this.focusInHandling.bind(this));
    this.addEventListener("focusout", this.focusOutHandling.bind(this));

    if (this.querySelector(".slideshow__autoplay")) {
      this.sliderAutoplayButton = this.querySelector(".slideshow__autoplay");
      this.sliderAutoplayButton.addEventListener("click", this.autoPlayToggle.bind(this));
      this.autoplayButtonIsSetToPlay = true;
      this.play();
    } else {
      this.reducedMotion.matches || this.announcementBarArrowButtonWasClicked ? this.pause() : this.play();
    }
  }

  onButtonClick(event) {
    super.onButtonClick(event);
    this.wasClicked = true;

    const isFirstSlide = this.currentPage === 1;
    const isLastSlide = this.currentPage === this.sliderItemsToShow.length;

    if (!isFirstSlide && !isLastSlide) {
      this.applyAnimationToAnnouncementBar(event.currentTarget.name);
      return;
    }

    if (isFirstSlide && event.currentTarget.name === "previous") {
      this.slideScrollPosition =
        this.slider.scrollLeft + this.sliderFirstItemNode.clientWidth * this.sliderItemsToShow.length;
    } else if (isLastSlide && event.currentTarget.name === "next") {
      this.slideScrollPosition = 0;
    }

    this.setSlidePosition(this.slideScrollPosition);
    this.applyAnimationToAnnouncementBar(event.currentTarget.name);
  }

  setSlidePosition(position) {
    if (this.setPositionTimeout) clearTimeout(this.setPositionTimeout);
    this.setPositionTimeout = setTimeout(() => {
      this.slider.scrollTo({
        left: position,
      });
    }, this.announcerBarAnimationDelay);
  }

  update() {
    super.update();
    this.sliderControlButtons = this.querySelectorAll(".slider-counter__link");
    this.prevButton.removeAttribute("disabled");

    if (!this.sliderControlButtons.length) return;

    this.sliderControlButtons.forEach((link) => {
      link.classList.remove("slider-counter__link--active");
      link.removeAttribute("aria-current");
    });
    this.sliderControlButtons[this.currentPage - 1].classList.add("slider-counter__link--active");
    this.sliderControlButtons[this.currentPage - 1].setAttribute("aria-current", true);
  }

  autoPlayToggle() {
    this.togglePlayButtonState(this.autoplayButtonIsSetToPlay);
    this.autoplayButtonIsSetToPlay ? this.pause() : this.play();
    this.autoplayButtonIsSetToPlay = !this.autoplayButtonIsSetToPlay;
  }

  focusOutHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (!this.autoplayButtonIsSetToPlay || focusedOnAutoplayButton) return;
      this.play();
    } else if (!this.reducedMotion.matches && !this.announcementBarArrowButtonWasClicked) {
      this.play();
    }
  }

  focusInHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (focusedOnAutoplayButton && this.autoplayButtonIsSetToPlay) {
        this.play();
      } else if (this.autoplayButtonIsSetToPlay) {
        this.pause();
      }
    } else if (this.announcementBarSlider.contains(event.target)) {
      this.pause();
    }
  }

  play() {
    this.slider.setAttribute("aria-live", "off");
    clearInterval(this.autoplay);
    this.autoplay = setInterval(this.autoRotateSlides.bind(this), this.autoplaySpeed);
  }

  pause() {
    this.slider.setAttribute("aria-live", "polite");
    clearInterval(this.autoplay);
  }

  togglePlayButtonState(pauseAutoplay) {
    if (pauseAutoplay) {
      this.sliderAutoplayButton.classList.add("slideshow__autoplay--paused");
      this.sliderAutoplayButton.setAttribute("aria-label", window.accessibilityStrings.playSlideshow);
    } else {
      this.sliderAutoplayButton.classList.remove("slideshow__autoplay--paused");
      this.sliderAutoplayButton.setAttribute("aria-label", window.accessibilityStrings.pauseSlideshow);
    }
  }

  autoRotateSlides() {
    const slideScrollPosition =
      this.currentPage === this.sliderItems?.length ? 0 : this.slider.scrollLeft + this.sliderItemOffset;

    this.setSlidePosition(slideScrollPosition);
    this.applyAnimationToAnnouncementBar();
  }

  setSlideVisibility(event) {
    this.sliderItemsToShow?.forEach((item, index) => {
      const linkElements = item.querySelectorAll("a") || [];
      if (index === this.currentPage - 1) {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.removeAttribute("tabindex");
          });
        item.setAttribute("aria-hidden", "false");
        item.removeAttribute("tabindex");
      } else {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.setAttribute("tabindex", "-1");
          });
        item.setAttribute("aria-hidden", "true");
        item.setAttribute("tabindex", "-1");
      }
    });
    this.wasClicked = false;
  }

  applyAnimationToAnnouncementBar(button = "next") {
    if (!this.announcementBarSlider || !this.sliderItems) return;

    const itemsCount = this.sliderItems.length;
    const increment = button === "next" ? 1 : -1;

    const currentIndex = this.currentPage - 1;
    let nextIndex = (currentIndex + increment) % itemsCount;
    nextIndex = nextIndex === -1 ? itemsCount - 1 : nextIndex;

    const nextSlide = this.sliderItems[nextIndex];
    const currentSlide = this.sliderItems[currentIndex];

    const animationClassIn = "announcement-bar-slider--fade-in";
    const animationClassOut = "announcement-bar-slider--fade-out";

    const isFirstSlide = currentIndex === 0;
    const isLastSlide = currentIndex === itemsCount - 1;

    const shouldMoveNext = (button === "next" && !isLastSlide) || (button === "previous" && isFirstSlide);
    const direction = shouldMoveNext ? "next" : "previous";

    currentSlide?.classList.add(`${animationClassOut}-${direction}`);
    nextSlide?.classList.add(`${animationClassIn}-${direction}`);

    setTimeout(() => {
      currentSlide?.classList.remove(`${animationClassOut}-${direction}`);
      nextSlide?.classList.remove(`${animationClassIn}-${direction}`);
    }, this.announcerBarAnimationDelay * 2);
  }

  linkToSlide(event) {
    event.preventDefault();
    const slideScrollPosition =
      this.slider.scrollLeft +
      this.sliderFirstItemNode.clientWidth *
        (this.sliderControlLinksArray.indexOf(event.currentTarget) + 1 - this.currentPage);
    this.slider.scrollTo({
      left: slideScrollPosition,
    });
  }
}

customElements.define("slideshow-component", SlideshowComponent);

class VariantSelects extends HTMLElement {
  constructor() {
    super();
    this.addEventListener("change", this.handleProductUpdate);
    this.initializeProductSwapUtility();
  }

  initializeProductSwapUtility() {
    this.swapProductUtility = new HTMLUpdateUtility();
    this.swapProductUtility.addPreProcessCallback((html) => {
      html.querySelectorAll(".scroll-trigger").forEach((element) => element.classList.add("scroll-trigger--cancel"));
      return html;
    });
    this.swapProductUtility.addPostProcessCallback((newNode) => {
      window?.Shopify?.PaymentButton?.init();
      window?.ProductModel?.loadShopifyXR();
      publish(PUB_SUB_EVENTS.sectionRefreshed, {
        data: {
          sectionId: this.dataset.section,
          resource: {
            type: SECTION_REFRESH_RESOURCE_TYPE.product,
            id: newNode.querySelector("variant-selects").dataset.productId,
          },
        },
      });
    });
  }

  handleProductUpdate(event) {
    const input = this.getInputForEventTarget(event.target);
    const targetId = input.id;
    const targetUrl = input.dataset.productUrl;
    this.currentVariant = this.getVariantData(targetId);
    const sectionId = this.dataset.originalSection || this.dataset.section;
    this.updateSelectedSwatchValue(event);
    this.toggleAddButton(true, "", false);
    this.removeErrorMessage();

    let callback = () => {};
    if (this.dataset.url !== targetUrl) {
      this.updateURL(targetUrl);
      this.updateShareUrl(targetUrl);
      callback = this.handleSwapProduct(sectionId);
    } else if (!this.currentVariant) {
      this.setUnavailable();
      callback = (html) => {
        this.updatePickupAvailability();
        this.updateOptionValues(html);
      };
    } else {
      this.updateURL(targetUrl);
      this.updateVariantInput();
      this.updateShareUrl(targetUrl);
      callback = this.handleUpdateProductInfo(sectionId);
    }

    this.renderProductInfo(sectionId, targetUrl, targetId, callback);
  }

  getSelectedOptionValues() {
    return Array.from(this.querySelectorAll("select, fieldset input:checked")).map(
      (element) => element.dataset.optionValueId,
    );
  }

  updateSelectedSwatchValue({ target }) {
    const { value, tagName } = target;

    if (tagName === "SELECT" && target.selectedOptions.length) {
      const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
      const selectedDropdownSwatchValue = target
        .closest(".product-form__input")
        .querySelector("[data-selected-value] > .swatch");
      if (!selectedDropdownSwatchValue) return;
      if (swatchValue) {
        selectedDropdownSwatchValue.style.setProperty("--swatch--background", swatchValue);
        selectedDropdownSwatchValue.classList.remove("swatch--unavailable");
      } else {
        selectedDropdownSwatchValue.style.setProperty("--swatch--background", "unset");
        selectedDropdownSwatchValue.classList.add("swatch--unavailable");
      }
    } else if (tagName === "INPUT" && target.type === "radio") {
      const selectedSwatchValue = target.closest(`.product-form__input`).querySelector("[data-selected-value]");
      if (selectedSwatchValue) selectedSwatchValue.innerHTML = value;
    }
  }

  updateMedia(html) {
    const mediaGallerySource = document.querySelector(`[id^="MediaGallery-${this.dataset.section}"] ul`);
    const mediaGalleryDestination = html.querySelector(`[id^="MediaGallery-${this.dataset.section}"] ul`);

    const refreshSourceData = () => {
      const mediaGallerySourceItems = Array.from(mediaGallerySource.querySelectorAll("li[data-media-id]"));
      const sourceSet = new Set(mediaGallerySourceItems.map((item) => item.dataset.mediaId));
      const sourceMap = new Map(mediaGallerySourceItems.map((item, index) => [item.dataset.mediaId, { item, index }]));
      return [mediaGallerySourceItems, sourceSet, sourceMap];
    };

    if (mediaGallerySource && mediaGalleryDestination) {
      let [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
      const mediaGalleryDestinationItems = Array.from(mediaGalleryDestination.querySelectorAll("li[data-media-id]"));
      const destinationSet = new Set(mediaGalleryDestinationItems.map(({ dataset }) => dataset.mediaId));
      let shouldRefresh = false;

      // add items from new data not present in DOM
      for (let i = mediaGalleryDestinationItems.length - 1; i >= 0; i--) {
        if (!sourceSet.has(mediaGalleryDestinationItems[i].dataset.mediaId)) {
          mediaGallerySource.prepend(mediaGalleryDestinationItems[i]);
          shouldRefresh = true;
        }
      }

      // remove items from DOM not present in new data
      for (let i = 0; i < mediaGallerySourceItems.length; i++) {
        if (!destinationSet.has(mediaGallerySourceItems[i].dataset.mediaId)) {
          mediaGallerySourceItems[i].remove();
          shouldRefresh = true;
        }
      }

      // refresh
      if (shouldRefresh) [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();

      // if media galleries don't match, sort to match new data order
      mediaGalleryDestinationItems.forEach((destinationItem, destinationIndex) => {
        const sourceData = sourceMap.get(destinationItem.dataset.mediaId);

        if (sourceData && sourceData.index !== destinationIndex) {
          mediaGallerySource.insertBefore(
            sourceData.item,
            mediaGallerySource.querySelector(`li:nth-of-type(${destinationIndex + 1})`),
          );

          // refresh source now that it has been modified
          [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
        }
      });
    }

    const mediaGallery = document.querySelector(`[id^="MediaGallery-${this.dataset.section}"]`);
    if (mediaGallery && this.currentVariant.featured_media?.id) {
      mediaGallery.setActiveMedia(`${this.dataset.section}-${this.currentVariant.featured_media.id}`);
    }

    // update media modal
    const modalContent = document.querySelector(`#ProductModal-${this.dataset.section} .product-media-modal__content`);
    const newModalContent = html.querySelector(`product-modal`);
    if (modalContent && newModalContent) modalContent.innerHTML = newModalContent.innerHTML;
  }

  updateURL(url) {
    if (this.dataset.updateUrl === "false") return;
    window.history.replaceState({}, "", `${url}${this.currentVariant?.id ? `?variant=${this.currentVariant.id}` : ""}`);
  }

  updateShareUrl(url) {
    const shareButton = document.getElementById(`Share-${this.dataset.section}`);
    if (!shareButton || !shareButton.updateUrl) return;
    shareButton.updateUrl(
      `${window.shopUrl}${url}${this.currentVariant?.id ? `?variant=${this.currentVariant.id}` : ""}`,
    );
  }

  updateVariantInput() {
    const productForms = document.querySelectorAll(
      `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`,
    );
    productForms.forEach((productForm) => {
      const input = productForm.querySelector('input[name="id"]');
      input.value = this.currentVariant.id;
      input.dispatchEvent(new Event("change", { bubbles: true }));
    });
  }

  updatePickupAvailability() {
    const pickUpAvailability = document.querySelector("pickup-availability");
    if (!pickUpAvailability) return;

    if (this.currentVariant && this.currentVariant.available) {
      pickUpAvailability.fetchAvailability(this.currentVariant.id);
    } else {
      pickUpAvailability.removeAttribute("available");
      pickUpAvailability.innerHTML = "";
    }
  }

  getInputForEventTarget(target) {
    return target.tagName === "SELECT" ? target.selectedOptions[0] : target;
  }

  getVariantData(inputId) {
    const element = this.getVariantDataElement(inputId);
    if (!element) {
      console.error(`Variant data not found for input ID: "${inputId}"`);
      return null;
    }
    return JSON.parse(element.textContent);
  }

  getVariantDataElement(inputId) {
    return this.querySelector(`script[type="application/json"][data-resource="${inputId}"]`);
  }

  removeErrorMessage() {
    const section = this.closest("section");
    if (!section) return;

    const productForm = section.querySelector("product-form");
    if (productForm) productForm.handleErrorMessage();
  }

  getWrappingSection(sectionId) {
    return (
      this.closest(`section[data-section="${sectionId}"]`) || // main-product
      this.closest(`quick-add-modal`)?.modalContent || // quick-add
      this.closest(`#shopify-section-${sectionId}`) || // featured-product
      null
    );
  }

  handleSwapProduct(sectionId) {
    return (html) => {
      const oldContent = this.getWrappingSection(sectionId);
      if (!oldContent) {
        return;
      }

      document.getElementById(`ProductModal-${sectionId}`)?.remove();

      const response =
        html.querySelector(`section[data-section="${sectionId}"]`) /* main/quick-add */ ||
        html.getElementById(`shopify-section-${sectionId}`); /* featured product*/

      this.swapProductUtility.viewTransition(oldContent, response);
    };
  }

  handleUpdateProductInfo(sectionId) {
    return (html) => {
      this.updatePickupAvailability();
      this.updateMedia(html);
      const priceDestination = document.getElementById(`price-${this.dataset.section}`);
      const priceSource = html.getElementById(`price-${sectionId}`);
      const skuSource = html.getElementById(`Sku-${sectionId}`);
      const skuDestination = document.getElementById(`Sku-${this.dataset.section}`);
      const inventorySource = html.getElementById(`Inventory-${sectionId}`);
      const inventoryDestination = document.getElementById(`Inventory-${this.dataset.section}`);

      const volumePricingSource = html.getElementById(`Volume-${sectionId}`);

      const pricePerItemDestination = document.getElementById(`Price-Per-Item-${this.dataset.section}`);
      const pricePerItemSource = html.getElementById(`Price-Per-Item-${sectionId}`);

      const volumePricingDestination = document.getElementById(`Volume-${this.dataset.section}`);
      const qtyRules = document.getElementById(`Quantity-Rules-${this.dataset.section}`);
      const volumeNote = document.getElementById(`Volume-Note-${this.dataset.section}`);

      if (volumeNote) volumeNote.classList.remove("hidden--important");
      if (volumePricingDestination) volumePricingDestination.classList.remove("hidden--important");
      if (qtyRules) qtyRules.classList.remove("hidden--important");
      if (priceSource && priceDestination) priceDestination.innerHTML = priceSource.innerHTML;
      if (inventorySource && inventoryDestination) inventoryDestination.innerHTML = inventorySource.innerHTML;
      if (skuSource && skuDestination) {
        skuDestination.innerHTML = skuSource.innerHTML;
        skuDestination.classList.toggle("hidden--important", skuSource.classList.contains("hidden--important"));
      }
      if (volumePricingSource && volumePricingDestination) {
        volumePricingDestination.innerHTML = volumePricingSource.innerHTML;
      }
      if (pricePerItemSource && pricePerItemDestination) {
        pricePerItemDestination.innerHTML = pricePerItemSource.innerHTML;
        pricePerItemDestination.classList.toggle(
          "hidden--important",
          pricePerItemSource.classList.contains("hidden--important"),
        );
      }

      const price = document.getElementById(`price-${this.dataset.section}`);
      if (price) price.classList.remove("hidden--important");

      if (inventoryDestination)
        inventoryDestination.classList.toggle("hidden--important", inventorySource.innerText === "");

      this.toggleAddButton(!this.currentVariant || !this.currentVariant.available, window.variantStrings.soldOut);

      this.updateOptionValues(html);

      publish(PUB_SUB_EVENTS.variantChange, {
        data: {
          sectionId,
          html,
          variant: this.currentVariant,
        },
      });
    };
  }

  updateOptionValues(html) {
    const variantSelects = html.querySelector("variant-selects");
    if (variantSelects) this.innerHTML = variantSelects.innerHTML;
  }

  renderProductInfo(sectionId, url, targetId, callback) {
    const params = this.currentVariant
      ? `variant=${this.currentVariant?.id}`
      : `option_values=${this.getSelectedOptionValues().join(",")}`;

    this.abortController?.abort();
    this.abortController = new AbortController();

    fetch(`${url}?section_id=${sectionId}&${params}`, {
      signal: this.abortController.signal,
    })
      .then((response) => response.text())
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, "text/html");
        callback(html);
      })
      .then(() => {
        // set focus to last clicked option value
        document.getElementById(targetId).focus();
      });
  }

  toggleAddButton(disable = true, text, modifyClass = true) {
    const productForm = document.getElementById(`product-form-${this.dataset.section}`);
    if (!productForm) return;
    const addButton = productForm.querySelector('[name="add"]');
    const addButtonText = productForm.querySelector('[name="add"] > span');
    if (!addButton) return;

    if (disable) {
      addButton.setAttribute("disabled", "disabled");
      if (text) addButtonText.textContent = text;
    } else {
      addButton.removeAttribute("disabled");
      addButtonText.textContent = window.variantStrings.addToCart;
    }
  }

  setUnavailable() {
    this.toggleAddButton(true, "", true);
    const button = document.getElementById(`product-form-${this.dataset.section}`);
    const addButton = button.querySelector('[name="add"]');
    const addButtonText = button.querySelector('[name="add"] > span');
    const price = document.getElementById(`price-${this.dataset.section}`);
    const inventory = document.getElementById(`Inventory-${this.dataset.section}`);
    const sku = document.getElementById(`Sku-${this.dataset.section}`);
    const pricePerItem = document.getElementById(`Price-Per-Item-${this.dataset.section}`);
    const volumeNote = document.getElementById(`Volume-Note-${this.dataset.section}`);
    const volumeTable = document.getElementById(`Volume-${this.dataset.section}`);
    const qtyRules = document.getElementById(`Quantity-Rules-${this.dataset.section}`);

    if (!addButton) return;
    addButtonText.textContent = window.variantStrings.unavailable;
    if (price) price.classList.add("hidden--important");
    if (inventory) inventory.classList.add("hidden--important");
    if (sku) sku.classList.add("hidden--important");
    if (pricePerItem) pricePerItem.classList.add("hidden--important");
    if (volumeNote) volumeNote.classList.add("hidden--important");
    if (volumeTable) volumeTable.classList.add("hidden--important");
    if (qtyRules) qtyRules.classList.add("hidden--important");
  }

  getInputSelector() {
    return 'variant-selects fieldset input[type="radio"], variant-selects select option';
  }
}

customElements.define("variant-selects", VariantSelects);

class ProductRecommendations extends HTMLElement {
  constructor() {
    super();
    this.observer = null;
  }

  connectedCallback() {
    this.initializeRecommendations();

    this.unsubscribeFromSectionRefresh = subscribe(PUB_SUB_EVENTS.sectionRefreshed, (event) => {
      const sectionId = this.dataset.sectionId;
      const isRelatedProduct = this.classList.contains("related-products");
      const isParentSectionUpdated = sectionId && (event.data?.sectionId ?? "") === `${sectionId.split("__")[0]}__main`;

      if (isRelatedProduct && isParentSectionUpdated) {
        this.dataset.productId = event.data.resource.id;
        this.initializeRecommendations();
      }
    });
  }

  disconnectedCallback() {
    this.unsubscribeFromSectionRefresh();
  }

  initializeRecommendations() {
    this.observer?.unobserve(this);
    this.observer = new IntersectionObserver(
      (entries, observer) => {
        if (!entries[0].isIntersecting) return;
        observer.unobserve(this);
        this.loadRecommendations();
      },
      { rootMargin: "0px 0px 400px 0px" },
    );
    this.observer.observe(this);
  }

  loadRecommendations() {
    fetch(`${this.dataset.url}&product_id=${this.dataset.productId}&section_id=${this.dataset.sectionId}`)
      .then((response) => response.text())
      .then((text) => {
        const html = document.createElement("div");
        html.innerHTML = text;
        const recommendations = html.querySelector("product-recommendations");

        if (recommendations?.innerHTML.trim().length) {
          this.innerHTML = recommendations.innerHTML;
        }

        if (!this.querySelector("slideshow-component") && this.classList.contains("complementary-products")) {
          this.remove();
        }

        if (html.querySelector(".grid__item")) {
          this.classList.add("product-recommendations--loaded");
        }
      })
      .catch((e) => {
        console.error(e);
      });
  }
}

customElements.define("product-recommendations", ProductRecommendations);

const setCookie = (cname, cvalue, exdays, cdomain) => {
  const HOURS_IN_THE_DAY = 24;
  const MINUTES_IN_THE_HOUR = 60;
  const SECONDS_IN_THE_MINUTE = 60;
  const MILLISECONDS_MULTIPLIER = 1000;

  const d = new Date();
  d.setTime(
    d.getTime() + exdays * HOURS_IN_THE_DAY * MINUTES_IN_THE_HOUR * SECONDS_IN_THE_MINUTE * MILLISECONDS_MULTIPLIER,
  );

  const getMainDomain = (hostname) => {
    const match = hostname.match(/^(?:www\.)?([^:]+)(?::\d+)?$/);
    return encodeURIComponent(match ? match[1] : hostname);
  };

  const domain = cdomain || getMainDomain(location.hostname);
  const expires = `expires=${d.toUTCString()}`;

  document.cookie = `${cname}=${encodeURIComponent(cvalue)};${expires};path=/${cdomain === location.hostname ? "" : ";domain=" + domain}`;
};

function getCookie(name) {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) {
    return parts.pop().split(";").shift();
  }
  return null;
}

function deleteCookie(name, successCallback = null) {
  const shopifyDomain = "shop.app";
  const fashionphileGlobalDomain = "." + location.hostname.replace(/^www\./, "");
  const hostDomain = location.hostname;
  const domainsToClear = [shopifyDomain, fashionphileGlobalDomain, hostDomain];
  const cookies = document.cookie.split(";");
  cookies.forEach((cookie) => {
    const nameCookie = cookie.split("=")[0].trim();
    if (nameCookie === name) {
      document.cookie = nameCookie + "=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
      domainsToClear.forEach((domain) => {
        document.cookie = nameCookie + "=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=" + domain;
      });
    }
  });
  if (typeof successCallback === "function") successCallback();
}

function deleteCookieByDomain(name, domain) {
  document.cookie = name + "=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=" + domain;
}

function deleteAllCookiesForDomain(domain) {
  const cookies = document.cookie.split(";");

  for (let i = 0; i < cookies.length; i++) {
    let cookie = cookies[i].trim();
    let name = cookie.split("=")[0];

    // Check if the cookie's domain matches the target domain (or a subdomain)
    // You might need a more robust domain matching logic depending on your needs
    if (document.domain.includes(domain) || cookie.includes(`domain=${domain}`)) {
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${domain}`;
    }
  }
}

window.addEventListener("load", () => {
  deleteAllCookiesForDomain("shop.app");
  deleteAllCookiesForDomain(".shop.app");
});

function getCookieNameWithEnv(hostName, suffix = "") {
  if (hostName.includes("staging.fashionphile.com")) {
    return `staging_${suffix}`;
  }
  if (hostName.includes("staging.fashionphile.co.uk")) {
    return `staging_uk_${suffix}`;
  }
  if (["localhost", "127.0.0.1"].includes(hostName)) {
    return `dev_${suffix}`;
  }
  if (
    hostName === "shopify.fashionphile.com" ||
    hostName === "www.fashionphile.com" ||
    hostName === "fashionphile.com"
  ) {
    return `fp_${suffix}`;
  }
  if (
    hostName === "shopify.fashionphile.co.uk" ||
    hostName === "www.fashionphile.co.uk" ||
    hostName === "fashionphile.co.uk"
  ) {
    return `fp_uk_${suffix}`;
  }
  return "";
}

function getEnvScopedCookieName(name, hostName = location.hostname) {
  const envName = getCookieNameWithEnv(hostName, name);
  if (!envName || envName.startsWith("fp_")) {
    return name;
  }
  return envName;
}

function isLocalDev() {
  return ["localhost", "127.0.0.1"].includes(location.hostname);
}

/**
 * Fetch with retry logic and exponential backoff
 * @param {string} url - URL to fetch
 * @param {number} retries - Number of retries (default: 3)
 * @param {number} baseDelay - Base delay in ms (default: 1000)
 * @returns {Promise<Response>}
 */
async function fetchWithRetryGlobal(url, retries = 3, baseDelay = 1000) {
  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      const res = await fetch(url);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return res;
    } catch (err) {
      if (attempt === retries) throw err;
      const delay = baseDelay * Math.pow(2, attempt);
      await new Promise((resolve) => setTimeout(resolve, delay));
    }
  }
}

/**
 * Fetch product's secondary image URL
 *
 * @async
 * @param {string} productHandle - the product's handle
 * @returns {Promise<string>} - the product's secondary image URL
 */
async function getSecondaryImageByHandle(productHandle) {
  try {
    if (!productHandle) {
      console.error("Product handle is required");
      return null;
    }
    const res = await fetchWithRetryGlobal(window.Shopify.routes.root + "products/" + productHandle + ".js");
    const product = await res.json();
    return product.images?.[1] ?? null;
  } catch (err) {
    console.error("Could not load secondary images for product", productHandle, err);
    return null;
  }
}

const toggleLoader = (mode = "hide") => {
  const loader = document.getElementById("fp-loader");
  if (!loader) return;
  if (mode === "show") {
    loader.classList.remove("hidden");
  } else {
    loader.classList.add("hidden");
  }
};

const toggleSavedSearchFollowingLoader = (mode = "hide") => {
  const loader = document.getElementById("fp-saved-search-following-loader");
  if (!loader) return;
  if (mode === "show") {
    loader.classList.remove("hidden--important");
  } else {
    loader.classList.add("hidden--important");
  }
};

const toggleCartLoader = (mode = "hide") => {
  const bubbleLoader = document.getElementById("fp-cart-bubble-loader");
  const cartLoader = document.getElementById("fp-cart-loader");
  if (cartLoader) {
    if (mode === "show") {
      cartLoader.classList.remove("hidden--important");
    } else {
      cartLoader.classList.add("hidden--important");
    }
  }
  if (bubbleLoader) {
    if (mode === "show") {
      bubbleLoader.classList.remove("hidden--important");
    } else {
      bubbleLoader.classList.add("hidden--important");
    }
  }
};

// Observes and updates dynamically added <link> elements
// to replace invalid 'prefetch' rel with 'preload' for accessibility compliance.
(function () {
  const linksObserver = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node.tagName === "LINK" && node.hasAttribute("as") && node.getAttribute("rel") === "prefetch") {
          node.setAttribute("rel", "preload");
        }
      });
    });
    debouncedDisconnect();
  });

  const debouncedDisconnect = debounce(() => {
    linksObserver.disconnect();
  }, 2000);

  linksObserver.observe(document.head, { childList: true });

  function hideAffirmIfSoldOut() {
    const affirmBlock = document.querySelector(
      '[id^="shopify-block"][id*="affirm_pay_over_time_messaging_product_block"]',
    );
    const isSoldOut = document.querySelector(".fp-find-another-button") !== null;
    if (isSoldOut && affirmBlock) {
      affirmBlock.style.display = "none";
    }
  }
  hideAffirmIfSoldOut();

  function hideAffirmIfGiftCardInCart() {
    const affirmBlock = document.querySelector(
      '[id^="shopify-block"][id*="affirm_pay_over_time_messaging_cart_block"]',
    );
    const mainCart = document.getElementById("main-cart");
    const hasGiftCard = mainCart?.dataset?.hasGiftCard === "true";
    if (hasGiftCard && affirmBlock) {
      affirmBlock.style.display = "none";
    }
  }
  hideAffirmIfGiftCardInCart();
})();

function numberWithCommas(x) {
  return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}

document.addEventListener("DOMContentLoaded", function () {
  const elements = document.querySelectorAll(".fp-js-number-with-commas");

  elements.forEach(function (element) {
    const unformattedNumber = element.textContent;
    const formattedNumber = numberWithCommas(unformattedNumber);
    element.textContent = formattedNumber;
  });
});

/**
 * Executes an array of async tasks in parallel with a maximum batch size.
 *
 * This function limits concurrency by splitting the input array into chunks
 * (`batch` size) and running each chunk in parallel with `Promise.allSettled`.
 * Any failed tasks are logged and skipped; only fulfilled results are returned.
 *
 * @template T,U
 * @param {T[]} arr - The array of input items to process.
 * @param {(item: T) => Promise<U>} fn - An async function that processes each item.
 * @param {number} [batch=5] - The maximum number of items to process in parallel per batch.
 *
 * @returns {Promise<U[]>} A promise that resolves with an array of successful results.
 *
 * @example
 * // Run API calls in batches of 3, ignoring failed ones
 * const results = await runParallel(users, fetchUserData, 3);
 * console.log(results); // only successful values
 */
async function runParallel({ arr, fn, batch = 5 }) {
  const result = [];
  for (let i = 0; i < arr.length; i += batch) {
    const chunk = arr.slice(i, i + batch);
    const res = await Promise.allSettled(chunk.map((x) => fn(x)));
    for (const r of res) {
      if (r.status === "fulfilled") {
        result.push(r.value);
      } else {
        console.error("Task failed:", r.reason);
      }
    }
  }
  return result; // only fulfilled values
}

/**
 * Checks if the email is valid based on the standard EMAIL_VALIDATION_REGEX.
 * @param {string} email - The email address to validate.
 * @returns {boolean} - True if valid, false otherwise.
 */
function isValidEmail(email) {
  return EMAIL_VALIDATION_REGEX.test(email);
}

function setupEmailValidation({
  inputSelector,
  buttonSelector,
  errorWrapperSelector,
  errorTextSelector,
  errorMessage = "Enter a valid email address, like name@example.com.",
}) {
  const emailInput = document.querySelector(inputSelector);
  const submitBtn = document.querySelector(buttonSelector);
  const errorMsg = document.querySelector(errorWrapperSelector);
  const errorText = document.querySelector(errorTextSelector);

  if (!emailInput || !submitBtn || !errorMsg || !errorText) return;

  const showError = () => {
    errorText.textContent = errorMessage;
    errorMsg.style.display = "block";
    submitBtn.disabled = true;
  };

  const hideError = () => {
    errorMsg.style.display = "none";
    submitBtn.disabled = false;
  };

  emailInput.addEventListener("blur", () => {
    isValidEmail(emailInput.value) ? hideError() : showError();
  });

  emailInput.addEventListener("input", () => {
    if (isValidEmail(emailInput.value)) hideError();
  });

  submitBtn.addEventListener("click", (e) => {
    if (!isValidEmail(emailInput.value)) {
      e.preventDefault();
      showError();
    }
  });
}

document.addEventListener("DOMContentLoaded", function () {
  setupEmailValidation({
    inputSelector: 'input[name="contact[email]"]',
    buttonSelector: "#Subscribe",
    errorWrapperSelector: ".newsletter-form__message",
    errorTextSelector: ".newsletter-error-text",
    errorMessage: "Enter a valid email address, like name@example.com.",
  });
});

function parseImageUrl(url) {
  if (!url) return null;
  if (url.startsWith("data:") || url.startsWith("blob:") || url.startsWith("javascript:")) return null;
  const normalized = url.startsWith("//") ? `https:${url}` : url;
  try {
    return new URL(normalized, location.origin);
  } catch {
    return null;
  }
}

function captureErrorWithScope(error, tags = {}, context = {}) {
  if (!error || !window.Sentry) {
    return;
  }
  window.Sentry?.withScope((scope) => {
    Object.entries(tags).forEach(([key, value]) => {
      scope.setTag(key, String(value));
    });
    Object.entries(context).forEach(([key, value]) => {
      const contextValue = typeof value === "object" && value !== null ? value : { value: String(value) };
      scope.setContext(key, contextValue);
    });
    window.Sentry.captureException(error);
  });
}

async function fetchProductByHandles(handles) {
  if (!handles.length) {
    return [];
  }
  const shopDomain = window.shopDomain || "";
  const storefrontToken = window.storefrontToken;
  const productParsedKeys = new Set(["categories", "product_material"]);
  const countryCode = window.marketCountryCode || "US";
  const query = `query @inContext(country: ${countryCode}) {
        ${handles
          .map(
            (handle, i) => `
          product${i}: product(handle: "${handle}") {
            handle
            id
            title
            availableForSale
            vendor
            productType
            variants(first: 1) {
              edges {
                node {
                  id
                  sku
                  price { amount }
                  compareAtPrice { amount }
                }
              }
            }
            images(first: 10) {
              edges {
                node { url altText }
              }
            }
            metafields(identifiers: [
              { namespace: "custom", key: "condition" },
              { namespace: "custom", key: "hold_status" },
              { namespace: "custom", key: "ships_with" },
              { namespace: "custom", key: "color" },
              { namespace: "custom", key: "db_condition" },
              { namespace: "custom", key: "discounted_tier" },
              { namespace: "custom", key: "discounted_rate" },
              { namespace: "custom", key: "style" },
              { namespace: "custom", key: "categories" },
              { namespace: "custom", key: "retail_price" },
              { namespace: "custom", key: "product_material" },
              { namespace: "swym_wishlist", key: "wishlist_social_count" }
            ]) {
              key
              namespace
              value
            }
          }
        `,
          )
          .join("")}
      }`;

  try {
    const url = `https://${shopDomain}/api/2025-04/graphql.json`;
    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Shopify-Storefront-Access-Token": storefrontToken,
      },
      body: JSON.stringify({ query }),
    });
    if (!response.ok) {
      throw new Error(`HTTP error: ${response.status}`);
    }

    const { data, errors } = await response.json();
    if (errors?.length) {
      console.error("GraphQL errors:", errors);
      return [];
    }

    const rawProducts = Object.values(data).filter(Boolean);

    const filteredProducts = rawProducts.filter((product) => product.availableForSale);

    return filteredProducts.map((product) => ({
      ...product,
      metafields: transformMetafields(product.metafields ?? [], productParsedKeys),
    }));
  } catch (error) {
    console.error("Error fetching products:", error);
    return [];
  }
}

function transformMetafields(metafields, parsedKeys = new Set()) {
  return Object.fromEntries(
    metafields.filter(Boolean).map(({ key, value }) => [key, parsedKeys.has(key) ? JSON.parse(value) : value]),
  );
}
